import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    PlusCircle, Trash2, Box, DollarSign, XCircle, Tag
} from 'lucide-react';

// Assuming these paths are correct relative to where you place this file:
import Button from './Button';
import Card from './Card';
import Input from './Input';
import SearchableSelectBox from './SearchableSelectBox';
// import Card from '../components/ui/Card';
// import Button from '../components/ui/Button';
// import Input from './Input'; // Your provided Input component
// import SearchableSelectBox from './SearchableSelectBox'; // Your provided SearchableSelectBox component

// Initial structure for a new row
const getNewRow = (id) => ({
    id: id,
    itemId: '',
    quantity: 1,
    rate: 0.00,
    discountId: '',
    taxId: '',
    amount: 0.00,
});

/**
 * Renders a single editable row for the invoice item table.
 * Handles calculation and in-place editing.
 */
const InvoiceItemRow = ({
    row,
    onChange,
    onDelete,
    allItemOptions,
    allDiscountOptions,
    allTaxOptions,
    currencySymbol = '₦',
    customerId = '',
    storeId = '',
    lineItems = []
}) => {
    const findOption = useCallback((id, options) => {
        return options.find(opt => opt.value === id);
    }, []);

    const selectedItem = findOption(row.itemId, allItemOptions);
    const isProduct = selectedItem?.type === 'product';
    const maxStock = isProduct ? (selectedItem?.total_quantity ?? 0) : null;

    // Recalculate amount + auto-fill rate when item changes
    useEffect(() => {
        if (!selectedItem) return;

        const tax = findOption(row.taxId, allTaxOptions);
        let price = parseFloat(row.rate || 0);
        const qty = parseFloat(row.quantity || 1);

        // Auto-fill rate from catalog if empty
        if (selectedItem.price && price === 0) {
            onChange(row.id, 'rate', selectedItem.price.toFixed(2));
            price = selectedItem.price;
        }

        const subtotal = price * qty;

        // ─────────────────────────────────────────────────────
        // SMART DISCOUNT ENGINE — FULLY VALIDATED
        // ─────────────────────────────────────────────────────
        // let finalDiscountAmount = 0;
        // const selectedDiscount = findOption(row.discountId, allDiscountOptions);

        // if (selectedDiscount && selectedDiscount.isActive) {
        //     const now = new Date();
        //     const start = selectedDiscount.startDate ? new Date(selectedDiscount.startDate) : null;
        //     const end = selectedDiscount.endDate ? new Date(selectedDiscount.endDate) : null;

        //     // Date range check
        //     if ((start && now < start) || (end && now > end)) {
        //         // Invalid date → ignore discount
        //     } else {
        //         const appliesToProduct = selectedDiscount.appliesTo === 'product';
        //         const appliesToOrder = selectedDiscount.appliesTo === 'order';

        //         const matchesProduct = selectedDiscount.targetProductId
        //             ? Number(selectedDiscount.targetProductId) === Number(row.itemId)
        //             : true;

        //         const matchesCustomer = selectedDiscount.customers.length === 0 ||
        //             selectedDiscount.customers.includes(Number(customerId));

        //         // const matchesStore = selectedDiscount.stores.length === 0 ||
        //         //     selectedDiscount.stores.includes(Number(storeId));
        //         const matchesStore = !storeId || 
        //             selectedDiscount.stores.length === 0 || 
        //             selectedDiscount.stores.includes(Number(storeId));

        //         const minQty = selectedDiscount.minimumPurchaseQuantity || 0;
        //         const minAmount = selectedDiscount.minimumPurchaseAmount || 0;

        //         const meetsQty = appliesToProduct
        //             ? qty >= minQty
        //             : lineItems.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) >= minQty;

        //         const meetsAmount = appliesToProduct
        //             ? subtotal >= minAmount
        //             : lineItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) >= minAmount;

        //         const canApply = matchesCustomer && matchesStore && meetsQty && meetsAmount &&
        //             (appliesToProduct ? matchesProduct : appliesToOrder);

        //         if (canApply) {
        //             if (selectedDiscount.type === 'percentage') {
        //                 finalDiscountAmount = subtotal * (selectedDiscount.valueAmount / 100);
        //             } else if (selectedDiscount.type === 'fixed') {
        //                 finalDiscountAmount = selectedDiscount.valueAmount;
        //             }
        //         }
        //     }
        // }


        // ─────────────────────────────────────────────────────
        // SMART DISCOUNT ENGINE — SUPPORTS PERCENTAGE, FIXED & TIERED
        // ─────────────────────────────────────────────────────
        let finalDiscountAmount = 0;
        const selectedDiscount = findOption(row.discountId, allDiscountOptions);

        if (selectedDiscount && selectedDiscount.isActive) {
            const now = new Date();
            const start = selectedDiscount.startDate ? new Date(selectedDiscount.startDate) : null;
            const end = selectedDiscount.endDate ? new Date(selectedDiscount.endDate) : null;

            if ((start && now < start) || (end && now > end)) {
                // expired
            } else {
                const appliesToProduct = selectedDiscount.appliesTo === 'product';
                const matchesProduct = selectedDiscount.targetProductId
                    ? Number(selectedDiscount.targetProductId) === Number(row.itemId)
                    : true;

                const matchesCustomer = !selectedDiscount.customers?.length ||
                    selectedDiscount.customers.includes(Number(customerId));

                const matchesStore = !selectedDiscount.stores?.length ||
                    selectedDiscount.stores.includes(Number(storeId));

                const qty = parseFloat(row.quantity || 1);
                const subtotal = parseFloat(row.rate || 0) * qty;

                const minQty = selectedDiscount.minimumPurchaseQuantity || 0;
                const minAmount = parseFloat(selectedDiscount.minimumPurchaseAmount || 0);

                const meetsQty = qty >= minQty;
                const meetsAmount = subtotal >= minAmount;

                const canApply = matchesCustomer && matchesStore && meetsQty && meetsAmount &&
                    (appliesToProduct ? matchesProduct : true);

                if (canApply) {
                    const discountValue = parseFloat(selectedDiscount.valueAmount || selectedDiscount.value || 0);

                    if (selectedDiscount.type === 'percentage' || selectedDiscount.discount_type === 'percentage') {
                        finalDiscountAmount = subtotal * (discountValue / 100);
                    }
                    else if (selectedDiscount.type === 'fixed' || selectedDiscount.discount_type === 'fixed') {
                        finalDiscountAmount = discountValue;
                    }
                    else if ((selectedDiscount.type === 'tiered_pricing' || selectedDiscount.discount_type === 'tiered_pricing')
                        && selectedDiscount.tiers?.length > 0) {

                        const applicableTier = selectedDiscount.tiers.find(tier =>
                            qty >= (tier.min_quantity || 0) &&
                            (!tier.max_quantity || qty <= tier.max_quantity)
                        );

                        if (applicableTier) {
                            finalDiscountAmount = parseFloat(applicableTier.tier_value || 0) * qty;
                        }
                    }
                }
            }
        }

        const taxable = subtotal - finalDiscountAmount;
        const taxRate = (tax?.rate || 0) / 100;
        const taxAmount = taxable * taxRate;
        const total = taxable + taxAmount;

        if (Math.abs(total - parseFloat(row.amount || 0)) > 0.01) {
            onChange(row.id, 'amount', total.toFixed(2));
        }
    }, [
        row.itemId,
        row.quantity,
        row.rate,
        row.discountId,
        row.taxId,
        selectedItem,
        allItemOptions,
        allDiscountOptions,
        allTaxOptions,
        onChange,
        row.id,
        row.amount,
        findOption,
        customerId,
        storeId,
        lineItems,
    ]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // ← NEW: Auto-correct quantity for products if exceeded
        if (name === 'quantity' && isProduct) {
            const numValue = parseFloat(value) || 0;
            if (numValue > maxStock) {
                // Revert to max stock and optionally show a toast (add useToast if needed)
                onChange(row.id, name, maxStock.toString());
                // Optional: alert or toast - uncomment if you have useToast
                // showToast(`Only ${maxStock} in stock!`, 'warning');
                return;
            }
        }

        onChange(row.id, name, value);
    };

    const isRateEditable = !row.itemId;

    // Exact same Quantity input as before — only added max + tiny stock text for products
    const QuantityInput = (
        <div className="relative">
            <Input
                type="number"
                name="quantity"
                value={row.quantity}
                onChange={handleChange}
                min="1"
                max={maxStock ?? undefined}
                placeholder="1"
                containerClassName="!mb-0"
                className="!text-sm !py-1 !px-2 text-center"
            />
            {isProduct && (
                <p className="text-xs text-gray-500 text-center mt-1">
                    Stock: {maxStock}
                </p>
            )}
        </div>
    );

    // Mobile version — unchanged layout
    const MobileRowContent = (
        <div className="md:hidden space-y-3 p-4 border-b border-gray-100 last:border-b-0">
            {/* Item */}
            <div className="flex flex-col gap-1 text-sm">
                <label className="block text-xs font-medium text-text-secondary mb-1 flex items-center gap-1">
                    <Tag size={14} className="text-gmail-blue" /> Item Details
                </label>
                <SearchableSelectBox
                    name="itemId"
                    value={row.itemId}
                    onChange={handleChange}
                    options={allItemOptions}
                    placeholder="Select Item..."
                    containerClassName="!mb-0"
                    className="!text-xs"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </div>

            {/* Qty + Rate */}
            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Qty</label>
                    {QuantityInput}
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Rate</label>
                    <Input
                        type="number"
                        name="rate"
                        value={row.rate}
                        onChange={handleChange}
                        min="0"
                        placeholder="0.00"
                        disabled={!isRateEditable}
                        containerClassName="!mb-0"
                        className="!text-sm !py-1 !px-2"
                    />
                </div>
            </div>

            {/* Discount + Tax */}
            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Discount</label>
                    <SearchableSelectBox
                        name="discountId"
                        value={row.discountId}
                        onChange={handleChange}
                        options={allDiscountOptions}
                        containerClassName="!mb-0"
                        className="!text-xs"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Tax</label>
                    <SearchableSelectBox
                        name="taxId"
                        value={row.taxId}
                        onChange={handleChange}
                        options={allTaxOptions}
                        containerClassName="!mb-0"
                        className="!text-xs"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
            </div>

            {/* Amount + Delete */}
            <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-100 mt-3">
                <div className="text-lg font-bold text-text-primary">
                    Amount: {currencySymbol}{parseFloat(row.amount).toFixed(2)}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 text-gmail-red hover:bg-red-50"
                    onClick={() => onDelete(row.id)}
                >
                    <Trash2 size={20} />
                </Button>
            </div>
        </div>
    );

    // Desktop version — 100% identical to your original
    const DesktopRowContent = (
        <tr className="hidden md:table-row border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
            <td className="px-3 py-2">
                <SearchableSelectBox
                    name="itemId"
                    value={row.itemId}
                    onChange={handleChange}
                    options={allItemOptions}
                    placeholder="Select Item..."
                    containerClassName="!mb-0"
                    className="!text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </td>
            <td className="w-20 px-3 py-2">
                {QuantityInput}
            </td>
            <td className="w-28 px-3 py-2">
                <Input
                    type="number"
                    name="rate"
                    value={row.rate}
                    onChange={handleChange}
                    min="0"
                    placeholder="0.00"
                    disabled={!isRateEditable}
                    containerClassName="!mb-0"
                    className="!text-sm !py-1 !px-2"
                />
            </td>
            <td className="w-32 px-3 py-2">
                <SearchableSelectBox
                    name="discountId"
                    value={row.discountId}
                    onChange={handleChange}
                    options={allDiscountOptions}
                    containerClassName="!mb-0"
                    className="!text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </td>
            <td className="w-32 px-3 py-2">
                <SearchableSelectBox
                    name="taxId"
                    value={row.taxId}
                    onChange={handleChange}
                    options={allTaxOptions}
                    containerClassName="!mb-0"
                    className="!text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </td>
            <td className="w-28 px-3 py-2 text-right">
                <span className="font-bold text-text-primary">
                    {currencySymbol}{parseFloat(row.amount).toFixed(2)}
                </span>
            </td>
            <td className="w-10 px-1 py-2 text-center">
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 text-gmail-red hover:bg-red-50"
                    onClick={() => onDelete(row.id)}
                >
                    <Trash2 size={16} />
                </Button>
            </td>
        </tr>
    );

    return (
        <>
            {DesktopRowContent}
            {MobileRowContent}
        </>
    );
};

/**
 * Main Invoice Items Table Component.
 * Manages the state of all item rows.
 */
const InvoiceItemsTable = ({
    // Props for reusable component
    itemOptions = [],
    discountOptions = [],
    taxOptions = [],
    currencySymbol = '$',
    onItemsChange,
    customerId = '',
    storeId = '',
    allItems = [],
    overrideItems = null
}) => {
    const [items, setItems] = useState([]);
    const [nextId, setNextId] = useState(1);

    const handleAddRow = () => {
        setItems(prev => [...prev, getNewRow(nextId)]);
        setNextId(prev => prev + 1);
    };

    const handleDeleteRow = useCallback((id) => {
        setItems(prev => prev.filter(row => row.id !== id));
    }, []);

    const handleRowChange = useCallback((id, name, value) => {
        setItems(prev => prev.map(row => {
            if (row.id === id) {
                return { ...row, [name]: value };
            }
            return row;
        }));
    }, []);

    useEffect(() => {
        if (onItemsChange) {
            onItemsChange(items);
        }
    }, [items, onItemsChange]);

    // Calculate invoice totals
    const totals = useMemo(() => {
        const subtotal = items.reduce((sum, item) => {
            return sum + parseFloat(item.amount || 0);
        }, 0);

        // NOTE: In a production app, you might re-calculate tax/discounts here 
        // if there are overall invoice adjustments. For this component, we use 
        // the sum of calculated line-item totals.

        return {
            subtotal: subtotal,
            grandTotal: subtotal,
        };
    }, [items]);

    useEffect(() => {
        if (overrideItems && Array.isArray(overrideItems) && overrideItems.length > 0) {
            setItems(overrideItems);

            // important: Update 'nextId' so if the user clicks "Add Item" manually later, 
            // it doesn't create a duplicate ID (e.g., if populated items have IDS 1, 2, 3, make sure next is 4)
            const maxId = overrideItems.reduce((max, item) => {
                return item.id > max ? item.id : max;
            }, 0)
            setNextId(maxId + 1)
        }
    }, [overrideItems]);

    return (
        <Card className="p-0 overflow-hidden">
            {/* Table Header / Card Header */}
            <div className="p-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                    <Box size={20} className="text-gmail-blue" /> Item Table
                </h2>
                <Button
                    variant="secondary"
                    onClick={handleAddRow}
                    size="sm"
                    className="flex items-center gap-1"
                >
                    <PlusCircle size={16} /> Add Item
                </Button>
            </div>

            {/* Desktop Table View */}
            <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left font-medium text-gray-700 bg-gray-50 border-b">
                            <th className="px-3 py-2 w-auto">Item Details</th>
                            <th className="px-3 py-2 w-20 text-center">Qty</th>
                            <th className="px-3 py-2 w-28 text-center">Rate</th>
                            <th className="px-3 py-2 w-32 text-center">Discount</th>
                            <th className="px-3 py-2 w-32 text-center">Tax</th>
                            <th className="px-3 py-2 w-28 text-right">Amount</th>
                            <th className="px-3 py-2 w-10"></th> {/* Delete button column */}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(row => (
                            <InvoiceItemRow
                                key={row.id}
                                row={row}
                                onChange={handleRowChange}
                                onDelete={handleDeleteRow}
                                allItemOptions={itemOptions}
                                allDiscountOptions={discountOptions}
                                allTaxOptions={taxOptions}
                                currencySymbol={currencySymbol}
                                customerId={customerId}
                                storeId={storeId}
                                lineItems={allItems}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile List View (Stackable Cards) */}
            <div className="md:hidden divide-y divide-gray-100">
                {items.length === 0 && (
                    <div className="p-4 text-center text-text-secondary">
                        <XCircle size={24} className="mx-auto mb-2 text-gray-400" />
                        <p>No items added yet. Click 'Add Item' to start.</p>
                    </div>
                )}
                {items.map(row => (
                    <InvoiceItemRow
                        key={row.id}
                        row={row}
                        onChange={handleRowChange}
                        onDelete={handleDeleteRow}
                        allItemOptions={itemOptions}
                        allDiscountOptions={discountOptions}
                        allTaxOptions={taxOptions}
                        currencySymbol={currencySymbol}
                        customerId={customerId}
                        storeId={storeId}
                        lineItems={allItems}
                    />
                ))}
            </div>


            {/* Totals Summary Footer */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <div className="w-full md:w-1/3 space-y-2 text-sm">
                    <div className="flex justify-between font-medium text-text-secondary">
                        <span>Subtotal:</span>
                        <span>{currencySymbol}{totals.subtotal.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between text-lg font-bold text-gmail-blue border-t pt-2 mt-2">
                        <span>Grand Total:</span>
                        <span>{currencySymbol}{totals.grandTotal.toFixed(2)}</span>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default InvoiceItemsTable;